import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useBrandManager } from "@/hooks/useBrandManager";
import { getEdgeFunctionErrorMessage } from "@/utils/edgeFunction";

interface SlackChannel {
  id: string;
  name: string;
}

export function useSlackApi() {
  const [isLoading, setIsLoading] = useState(false);
  const { currentBrandId } = useBrandManager();

  const sendMessage = async (channel: string, text: string) => {
    if (!currentBrandId) {
      toast.error("No brand selected");
      return null;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('slack-messaging', {
        body: {
          operation: 'send_message',
          channel,
          text,
          brandId: currentBrandId
        }
      });

      if (error) {
        const errMsg = getEdgeFunctionErrorMessage(error as any, 'slack-messaging');
        toast.error(errMsg);
        return null;
      }

      if (data?.success) {
        toast.success("Message sent to Slack!");
        return data;
      } else {
        toast.error(data?.error || "Failed to send message");
        return null;
      }
    } catch (error: any) {
      const errMsg = getEdgeFunctionErrorMessage(error as any, 'slack-messaging');
      toast.error(errMsg);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const sendRichMessage = async (channel: string, blocks: any[]) => {
    if (!currentBrandId) {
      toast.error("No brand selected");
      return null;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('slack-messaging', {
        body: {
          operation: 'send_rich_message',
          channel,
          blocks,
          brandId: currentBrandId
        }
      });

      if (error) {
        const errMsg = getEdgeFunctionErrorMessage(error as any, 'slack-messaging');
        toast.error(errMsg);
        return null;
      }

      if (data?.success) {
        toast.success("Rich message sent to Slack!");
        return data;
      } else {
        toast.error(data?.error || "Failed to send rich message");
        return null;
      }
    } catch (error: any) {
      const errMsg = getEdgeFunctionErrorMessage(error as any, 'slack-messaging');
      toast.error(errMsg);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const sendNotification = async (text: string, channelOverride?: string, brandIdOverride?: string) => {
    const brandId = brandIdOverride || currentBrandId;
    if (!brandId) {
      toast.error("No brand selected");
      return null;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('slack-messaging', {
        body: {
          operation: 'send_notification',
          text,
          channel: channelOverride,
          brandId
        }
      });

      if (error) {
        const errMsg = getEdgeFunctionErrorMessage(error as any, 'slack-messaging');
        toast.error(errMsg);
        return null;
      }

      if (data?.success) {
        toast.success("Notification sent!");
        return data;
      } else {
        const errorMsg = data?.error?.includes('not configured') || data?.error?.includes('Bot Token')
          ? "No Slack configured for this brand"
          : data?.error || "Failed to send notification";
        toast.error(errorMsg);
        return null;
      }
    } catch (error: any) {
      const errMsg = getEdgeFunctionErrorMessage(error as any, 'slack-messaging');
      toast.error(errMsg);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const listChannels = async (): Promise<SlackChannel[]> => {
    if (!currentBrandId) {
      toast.error("No brand selected");
      return [];
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('slack-messaging', {
        body: {
          operation: 'list_channels',
          brandId: currentBrandId
        }
      });

      if (error) {
        const errMsg = getEdgeFunctionErrorMessage(error as any, 'slack-messaging');
        toast.error(errMsg);
        return [];
      }

      if (data?.success && data?.channels) {
        return data.channels;
      } else {
        toast.error(data?.error || "Failed to list channels");
        return [];
      }
    } catch (error: any) {
      const errMsg = getEdgeFunctionErrorMessage(error as any, 'slack-messaging');
      toast.error(errMsg);
      return [];
    } finally {
      setIsLoading(false);
    }
  };

  return {
    sendMessage,
    sendRichMessage,
    sendNotification,
    listChannels,
    isLoading
  };
}
